import type { AdminFeeTax } from 'application-types';
import { CURRENCY_CONVERSION } from 'lane-shared/helpers/constants';
import { TAX_VALUE_CONVERSION } from 'lane-shared/domains/billingPayments/constants';
import {
  TaxAmountType,
  type AddProductToChargeDetails,
} from 'lane-shared/domains/billingPayments/types';

type ProductAdminFeeTaxSource = {
  adminFeeTax?: AdminFeeTax | null;
};

/**
 * Billing & Payment stores admin fee tax like {@link AdminFeeTaxRate}:
 * - percentage: `rate = displayPercent × TAX_VALUE_CONVERSION` (e.g. 15% → 1500)
 * - fixed: `rate` in minor currency units (e.g. $20.00 → 2000 when CURRENCY_CONVERSION is 100)
 *
 * Charge line + `getPricing` expect human percent (e.g. "15") and major-unit fixed ("20").
 */
export function adminFeeTaxFromChannelStorageToLineFields(
  tax: AdminFeeTax
): { type: TaxAmountType; value: string } {
  const type = tax.taxAmountType;

  if (type === TaxAmountType.TaxAmountTypeFixed) {
    return {
      type,
      value: String(tax.rate / CURRENCY_CONVERSION),
    };
  }

  return {
    type:
      type === TaxAmountType.TaxAmountTypePercentage
        ? type
        : TaxAmountType.TaxAmountTypePercentage,
    value: String(tax.rate / TAX_VALUE_CONVERSION),
  };
}

export function hasCatalogProductOwnAdminFeeTax(
  selectedProduct: ProductAdminFeeTaxSource
): boolean {
  const rate = selectedProduct.adminFeeTax?.rate;

  return rate != null && !Number.isNaN(Number(rate)) && Number(rate) > 0;
}

export function hasPositiveAdminFeeTaxOnChargeLine(
  adminFeeTax: AddProductToChargeDetails['adminFeeTax'] | undefined
): boolean {
  if (!adminFeeTax?.value) {
    return false;
  }

  const n = Number(adminFeeTax.value);

  return !Number.isNaN(n) && n > 0;
}

/**
 * Resolves admin fee tax for a charge line when picking a catalog product:
 * 1) Product/service own admin fee tax when defined
 * 2) Else channel default from Billing & Payment when configured (non-null)
 * 3) Else omit separate admin fee tax (pricing falls back to product sales tax)
 *
 * Note: `channelAdminFeeTax` from `useBillingPaymentSettingsForChannel` is
 * `null` exactly when the channel has no admin fee tax configured, so we do
 * not need a separate "enabled" flag at the read site.
 */
export function adminFeeTaxFieldsForSelectedChargeProduct(
  isAdminFeeFeatureEnabled: boolean,
  channelAdminFeeTax: AdminFeeTax | null,
  selectedProduct: ProductAdminFeeTaxSource
): { type: TaxAmountType; value: string } {
  if (!isAdminFeeFeatureEnabled) {
    return {
      type:
        selectedProduct?.adminFeeTax?.taxAmountType ||
        TaxAmountType.TaxAmountTypePercentage,
      value: selectedProduct?.adminFeeTax?.rate?.toString() ?? '0',
    };
  }

  if (hasCatalogProductOwnAdminFeeTax(selectedProduct)) {
    const catalogTax = selectedProduct.adminFeeTax;

    return {
      type:
        catalogTax?.taxAmountType ?? TaxAmountType.TaxAmountTypePercentage,
      value: String(catalogTax?.rate),
    };
  }

  if (channelAdminFeeTax) {
    return adminFeeTaxFromChannelStorageToLineFields(channelAdminFeeTax);
  }

  return {
    type: TaxAmountType.TaxAmountTypePercentage,
    value: '',
  };
}

export function mergeProductDetailsForChargePricing(
  productDetails: AddProductToChargeDetails,
  isAdminFeeFeatureEnabled: boolean,
  channelAdminFeeTax: AdminFeeTax | null
): AddProductToChargeDetails {
  if (!isAdminFeeFeatureEnabled) {
    return productDetails;
  }

  if (hasPositiveAdminFeeTaxOnChargeLine(productDetails.adminFeeTax)) {
    return productDetails;
  }

  if (channelAdminFeeTax) {
    return {
      ...productDetails,
      adminFeeTax: adminFeeTaxFromChannelStorageToLineFields(channelAdminFeeTax),
    };
  }

  return {
    ...productDetails,
    adminFeeTax: {
      type: TaxAmountType.TaxAmountTypePercentage,
      value: '',
    },
  };
}
